import { queryOptions } from '@tanstack/react-query'
import type {
  AccessGroupListResponse,
  Department,
  DepartmentInput,
  DepartmentListResponse,
} from '@/types/departmentTypes'

// Department permissions. Change rarely, so no polling — the manage dialog
// invalidates ['departments'] after every write.
export const departmentsQueryOptions = () =>
  queryOptions({
    queryKey: ['departments'],
    queryFn: async (): Promise<DepartmentListResponse> => {
      const response = await fetch('/api/departments')
      if (!response.ok) {
        throw new Error('Failed to fetch departments')
      }
      return response.json()
    },
    staleTime: 60_000,
  })

// The SMS.Access.* groups that exist in the directory, so a department can be
// pointed at a real one instead of an object id pasted in by hand.
export const accessGroupsQueryOptions = () =>
  queryOptions({
    queryKey: ['accessGroups'],
    queryFn: async (): Promise<AccessGroupListResponse> => {
      const response = await fetch('/api/admin/accessGroups')
      if (!response.ok) {
        throw new Error('Failed to fetch access groups')
      }
      return response.json()
    },
    staleTime: 60_000,
  })

async function unwrap(response: Response, fallback: string) {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || fallback)
  }
  return response.json()
}

export const createDepartment = async (data: DepartmentInput): Promise<Department> =>
  unwrap(
    await fetch('/api/departments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
    'Failed to create department',
  )

export const updateDepartment = async (
  id: string,
  data: DepartmentInput,
): Promise<Department> =>
  unwrap(
    await fetch(`/api/departments/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
    'Failed to update department',
  )

export const deleteDepartment = async (id: string): Promise<void> => {
  const response = await fetch(`/api/departments/${id}`, { method: 'DELETE' })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to delete department')
  }
}
